/**
 * Sticky Add to Cart
 *
 * A custom element that renders a fixed bottom bar on mobile with:
 *  - Product title
 *  - Variant selector (hidden native <select> over a visible label)
 *  - Quantity input
 *  - Add to Cart button (respects Coming Soon / holiday-shutdown / sold-out states)
 *
 * Syncs with the main variant picker on the page by subscribing to
 * PUB_SUB_EVENTS.variantChange (already fired by product-info.js).
 */

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        // DOM references
        this.variantLabel = this.querySelector('.sticky-atc__variant-label');
        this.variantSelect = this.querySelector('.sticky-atc__variant-select');
        this.priceContainer = this.querySelector(`#sticky-atc-price-${this.sectionId}`);

        // Subscribe to variant changes triggered by the main product picker
        this._variantChangeUnsubscribe = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        // When the user changes the variant via the sticky bar's native select,
        // sync that choice back to the main page picker
        if (this.variantSelect) {
          this.variantSelect.addEventListener('change', this.onStickySelectChange.bind(this));
        }

      }

      disconnectedCallback() {
        if (this._variantChangeUnsubscribe) this._variantChangeUnsubscribe();
      }

      // ── Getters for elements that may be replaced by product-form.js ──

      get variantInput() {
        return this.querySelector('input.product-variant-id');
      }

      get submitButton() {
        return this.querySelector('[type="submit"]');
      }

      get submitButtonText() {
        return this.submitButton?.querySelector('span');
      }

      // ── Handler: main page variant changed ──

      onVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;

        const { variant, html } = event.data;

        // 1. Sync hidden variant ID input so the sticky form submits the right product
        if (this.variantInput) {
          this.variantInput.value = variant ? variant.id : '';
          this.variantInput.disabled = false;
        }

        // 2. Sync the native select value
        if (this.variantSelect && variant) {
          this.variantSelect.value = String(variant.id);
        }

        // 3. Update the visible label
        if (this.variantLabel && variant) {
          this.variantLabel.textContent = variant.option1;
        }

        // 4. Update price — prefer the freshly-fetched HTML to avoid timing issues
        const priceSource = html
          ? html.getElementById(`price-${this.sectionId}`)
          : document.getElementById(`price-${this.sectionId}`);

        if (priceSource && this.priceContainer) {
          this.priceContainer.innerHTML = priceSource.innerHTML;
        }

        // 5. Update button state
        this.updateButtonState(variant);
      }

      // ── Handler: user picked a variant in the sticky bar ──

      onStickySelectChange() {
        const variantId = this.variantSelect.value;
        const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
        const option1Value = selectedOption?.dataset.option1;

        // Sync hidden input immediately so the form is always up to date
        if (this.variantInput) {
          this.variantInput.value = variantId;
          this.variantInput.disabled = false;
        }

        // Trigger the main page's pill picker — this fires the full variant-change
        // flow (fetch new HTML, update price/media, publish variantChange) so the
        // main page stays in sync with whatever was chosen in the sticky bar.
        if (option1Value) {
          const mainPicker = document.querySelector(
            `variant-selects[data-section="${this.sectionId}"]`
          );
          if (mainPicker) {
            const matchingRadio = [...mainPicker.querySelectorAll('input[type="radio"]')].find(
              (input) => input.value === option1Value
            );
            if (matchingRadio) {
              matchingRadio.click();
              // onVariantChange will fire after the click → no need to manually
              // update label/price/button here.
              return;
            }
          }
        }

        // Fallback (e.g. if main picker isn't found): update button state from
        // the variant metafields data we already have on the page.
        const variantData = window.variantMetafields?.[variantId];
        const mockVariant = {
          id: parseInt(variantId),
          available: true, // optimistic — we don't have full availability data here
          option1: option1Value || selectedOption?.text,
          ...variantData,
        };
        if (this.variantLabel) this.variantLabel.textContent = mockVariant.option1;
        this.updateButtonState(mockVariant);
      }

      // ── Button state ──

      updateButtonState(variant) {
        if (!this.submitButton || !this.submitButtonText) return;

        const isHolidayShutdown = window.productTags?.includes('holiday-shutdown');
        const isComingSoon =
          variant && window.variantMetafields?.[variant.id]?.coming_soon === true;
        const isBookLaunch = window.productTags?.includes('book-launch');

        if (!variant) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent =
            window.variantStrings?.unavailable || 'Unavailable';
        } else if (isHolidayShutdown) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = 'Closed for Holiday Break';
        } else if (isComingSoon) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = 'Coming Soon';
        } else if (!variant.available) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = isBookLaunch
            ? 'Available on 18 October'
            : window.variantStrings?.soldOut || 'Sold out';
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            window.variantStrings?.addToCart || 'Add to cart';
        }
      }
    }
  );
}
